import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    ACTIVE,
    ASC,
    LOCAL_STORAGE_SESSION_ID,
    LOCAL_STORAGE_USER_ID,
} from "const"
import { MtAccount } from "server/models"
import * as routes from "client/parts/routes"
import { randomString, useInput } from "client/utils"
import {
    StoreType,

    LoadUserByLoginOrEmail,
    EncryptUserPassword,
    LoadAllAccounts,
    LoadAllAccountPackages,
    LoadUserAccounts,
    SortAccounts,

    initUserData,
    setAccountList,
    setAccountIds,
    setAccountPackageList,
} from "client/store"

import { Footer } from "client/parts/layout"
import {
    Spinner,
    TriangleRightIcon,
    Feedback,
    LostConnection,
} from "client/components"

import { setServerUserData } from "client/socketApi"

import {
    AuthWrapper,
    AuthTitle,
    AuthFormWrapper,
    SignInForm,
    SignInInput,
    SignInButton,
    AuthLinkWrapper,
    AuthLink,
} from "./styled"
import SelectAccount from "./selectAccount"

const shakeLength = 400
const userData = { ...initUserData }
userData.sessionId = `SESSION_${randomString(30)}`

const Login = () => {
    const [username, setUsername] = useInput("")
    const [password, setPassword] = useInput("")
    const [error, setError] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [isShaking, setIsShaking] = useState(false)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const accountIds = useSelector((state: StoreType) => state.account.accountIds)
    const lostServerConnection = useSelector((state: StoreType) => state.system.lostServerConnection)
    const dispatch = useDispatch()

    const onKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") onLogin()
    }

    const setUserData = (account: MtAccount) => {
        userData.accountId = account.id
        userData.accountName = account.name
        userData.accountLogo = account.logo
        setServerUserData(userData)
    }

    const onLogin = () => {
        if (isLoading) return
        if (!username || !password) {
            setIsShaking(true)
            setTimeout(() => {
                setIsShaking(false)
            }, shakeLength)
        } else {
            setIsLoading(true)
            setError("")

            LoadUserByLoginOrEmail(username).then((user) => {
                if (!user) {
                    setIsLoading(false)
                    setError(lang.authErrorNoUser)
                    return
                }

                let authorized = false
                if (process.env.NODE_ENV === "development") {
                    authorized = true // don't verify user password on localhost
                }
                if (process.env.NODE_ENV === "production") {
                    const passwordEncrypted = EncryptUserPassword(password)
                    authorized = passwordEncrypted === user.password
                }

                if (!authorized) {
                    setIsLoading(false)
                    setError(lang.authErrorWrongUsernamePassword)
                    return
                }

                userData.userId = user.id
                userData.userName = `${user.firstName} ${user.lastName}`
                userData.userLogo = user.logo
                userData.userEmail = user.email

                window.localStorage.setItem(LOCAL_STORAGE_USER_ID, userData.userId.toString())
                window.localStorage.setItem(LOCAL_STORAGE_SESSION_ID, userData.sessionId)

                Promise.all([
                    LoadAllAccounts(),
                    LoadAllAccountPackages(),
                    LoadUserAccounts(user.id),
                ]).then(([
                    accountList,
                    accountPackageList,
                    userAccountIds,
                ]) => {
                    const sortedAccountIds = SortAccounts(Object.values(accountList), "name", ASC, { status: ACTIVE })
                    const resultIds: number[] = []

                    if (userAccountIds.includes(1)) resultIds.push(1) // Super Admin on top
                    sortedAccountIds.forEach((id) => {
                        if (id !== 1 && userAccountIds.includes(id)) resultIds.push(id)
                    })

                    if (resultIds.length === 0) { // user is not added to any account
                        setIsLoading(false)
                        setError(lang.authErrorNoAccount)
                    } else if (resultIds.length === 1) {
                        const id = resultIds[0]
                        const account = accountList[id]

                        if (account.id === 1) { // login as Super Admin
                            setUserData(account)
                            return
                        }
                        if (account.demoExpiry !== null) {
                            const now = new Date()
                            const demoExpiry = new Date(account.demoExpiry)
                            if (demoExpiry < now) {
                                setIsLoading(false)
                                setError(lang.authErrorDemoAccountExpired)
                                return
                            }
                        }

                        setUserData(account)
                    } else {
                        setIsLoading(false)
                        setError("")
                        dispatch(setAccountList(accountList))
                        dispatch(setAccountIds(resultIds))
                        dispatch(setAccountPackageList(accountPackageList))
                    }
                })
            })
        }
    }

    return (
        <AuthWrapper>
            { lostServerConnection && <LostConnection /> }

            <AuthFormWrapper show={accountIds === null}>
                <AuthTitle>{lang.signIn}</AuthTitle>
                <SignInForm
                    shake={isShaking}
                    animLength={shakeLength}
                >
                    <SignInInput
                        type="text"
                        name="username"
                        value={username}
                        placeholder={lang.usernamePlaceholder}
                        onChange={setUsername}
                        onKeyPress={onKeyPress}
                    />
                    <SignInInput
                        type="password"
                        name="password"
                        value={password}
                        placeholder={lang.passwordPlaceholder}
                        onChange={setPassword}
                        onKeyPress={onKeyPress}
                    />
                    <SignInButton onClick={onLogin}>
                        {
                            isLoading
                                ? (
                                    <Spinner
                                        points={12}
                                        pointSize={3}
                                        size={26}
                                    />
                                )
                                : <TriangleRightIcon />
                        }
                    </SignInButton>

                    <AuthLinkWrapper>
                        <AuthLink href={routes.forgotPasswordRoute}>{lang.forgotPassword}</AuthLink>
                        {
                        /*
                            TODO: temporarily disabled
                            <AuthLink href={routes.registerRoute}>{lang.startFreeTrial}</AuthLink>
                        */
                        }
                    </AuthLinkWrapper>

                    <Feedback>{error}</Feedback>
                </SignInForm>
            </AuthFormWrapper>
            <SelectAccount
                show={accountIds !== null}
                setUserData={setUserData}
            />

            <Footer />
        </AuthWrapper>
    )
}
export default Login
